// Reminder pop-up (2026-10-04): shows the first due reminder from lib/reminders.js on any page, in any version, until it is
// dismissed or snoozed. The design-choice reminder offers: open the notes, keep one version (records a DECISION note on the
// server, switches the interface to it, dismisses), remind tomorrow, or dismiss.
import React, { useState, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { REMINDERS, dueReminders, readState, writeState, dismiss, snoozeUntilTomorrow } from '../lib/reminders'
import { UI_MODES, setUiMode, useUiMode } from '../lib/uiMode'
import { notes as notesApi } from '../lib/api'

const MONO = { fontFamily: 'JetBrains Mono, monospace' }
const SANS = { fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif' }

export default function ReminderModal() {
  const [state, setState] = useState(() => readState())
  const [now, setNow] = useState(() => new Date())
  const [busy, setBusy] = useState(false)
  const [hiddenNow, setHiddenNow] = useState(false)   // 'Open my notes' hides the pop-up for this page view; it returns on the next visit
  const mode = useUiMode()
  const location = useLocation()

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000)   // appears at midnight even if the page was left open
    return () => clearInterval(id)
  }, [])

  const due = useMemo(() => dueReminders(now, state, REMINDERS), [now, state])
  if (due.length === 0 || hiddenNow) return null
  const r = due[0]
  const apply = (next) => { setState(next); writeState(next) }

  const keep = async (choice) => {
    setBusy(true)
    try { await notesApi.add(`DECISION: keep ${choice === 'orig' ? 'ORIG' : choice.toUpperCase()} (chosen from the review reminder)`, mode, location.pathname) } catch { /* the choice still applies; the note is a record */ }
    setUiMode(choice)
    apply(dismiss(state, r.id))
    setBusy(false)
  }
  const openNotes = () => { setHiddenNow(true); window.dispatchEvent(new CustomEvent('sf:open-notes')) }

  const btn = { ...MONO, fontSize: '0.74rem', fontWeight: 700, minHeight: 40, padding: '0 14px', borderRadius: 10, border: '1px solid #334155', background: '#111c31', color: '#e2e8f0', cursor: 'pointer' }
  return (
    <div role="alertdialog" aria-labelledby="reminder-title" aria-describedby="reminder-body"
      style={{ position: 'fixed', inset: 0, zIndex: 90, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '72px 12px 12px', background: 'rgba(2,6,23,0.55)' }}>
      <div style={{ ...SANS, width: 'min(560px, 100%)', background: '#0f172a', border: '1px solid #f59e0b', borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,0.6)', padding: 20 }}>
        <div style={{ ...MONO, fontSize: '0.64rem', letterSpacing: '0.08em', color: '#fbbf24' }}>REMINDER · TUESDAY</div>
        <h2 id="reminder-title" style={{ margin: '4px 0 6px', fontSize: '1.25rem', fontWeight: 650, color: '#f1f5f9' }}>{r.title}</h2>
        <p id="reminder-body" style={{ margin: 0, fontSize: '0.92rem', lineHeight: 1.5, color: '#cbd5e1' }}>{r.body}</p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
          <button style={{ ...btn, background: '#38bdf8', color: '#04121c', borderColor: '#38bdf8' }} onClick={openNotes}>Open my notes</button>
        </div>
        <p style={{ margin: '16px 0 6px', fontSize: '0.8rem', color: '#94a3b8' }}>Ready to choose? Keep one (this saves a note and switches to it):</p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {r.choices.map(c => {
            const m = UI_MODES.find(x => x.key === c)
            return <button key={c} disabled={busy} style={btn} title={m && m.title} onClick={() => keep(c)}>Keep {m ? m.label : c}</button>
          })}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16, borderTop: '1px solid #1e293b', paddingTop: 12 }}>
          <button style={btn} onClick={() => apply(snoozeUntilTomorrow(state, r.id))}>Not yet: remind me tomorrow</button>
          <button style={{ ...btn, color: '#94a3b8' }} onClick={() => apply(dismiss(state, r.id))}>Dismiss</button>
        </div>
      </div>
    </div>
  )
}
